import {
  convertDateToYYYYMMDDFormat,
  formatDataToLabelValue,
  STATUSPAGE,
} from '@repo/ui';
import { ReportType } from 'domain/models';
import { GetAllCoursesUserUseCaseInterface } from 'domain/services/courses/get-all-courses-user';
import {
  GetAllEntitiesUseCaseInterface,
  GetAllSemestersUserUseCaseInterface,
  ReportRadinoUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

export const useReportRadinos = () => {
  const { entitiesService, reportService } = useService();

  const [organizationList, setOrganizationList] =
    useState<GetAllEntitiesUseCaseInterface.Result>([]);

  const [coursesList, setCoursesList] =
    useState<GetAllCoursesUserUseCaseInterface.Result>([]);

  const [semestersList, setSemestersList] =
    useState<GetAllSemestersUserUseCaseInterface.Result>([]);

  const [requestsStatus, setRequestsStatus] = useState({
    organizationLoading: STATUSPAGE.DONE,
    coursesUser: STATUSPAGE.DONE,
    reportRadinoPdf: STATUSPAGE.DONE,
    reportRadinoExcel: STATUSPAGE.DONE,
    semestersLoading: STATUSPAGE.DONE,
  });
  const date = new Date();
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      period: [new Date(firstDayOfMonth), new Date(currentDay)],
      roleIds: [],
      semesterIds: [],
      courseHashIds: [],
      organizationHashIds: [],
    },
    validationSchema: Yup.object({
      course: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
      entity: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string().nullable(),
            value: Yup.string().nullable(),
          })
          .nullable(),
      ),
    }),

    onSubmit: () => {
      // This method is not used anymore, submitReport is used instead
    },
  });

  // Formatação das organizações para o Select
  const organizationOptions = useMemo(
    () => formatDataToLabelValue(organizationList as never, 'id', 'name'),
    [organizationList],
  );

  // Formatação dos cursos para o Select
  const courseListOptions = useMemo(
    () => formatDataToLabelValue(coursesList as never, 'id', 'nome'),
    [coursesList],
  );

  const changeRequestStatus = (
    key: keyof typeof requestsStatus,
    status: STATUSPAGE,
  ) => {
    setRequestsStatus((prevState) => ({
      ...prevState,
      [key]: status,
    }));
  };

  // Listagem de organizações
  const requestGetAllOrganizations = useCallback(() => {
    changeRequestStatus('organizationLoading', STATUSPAGE.LOADING);
    return entitiesService
      .getAllEntities()
      .then((res) => {
        setOrganizationList(res);
      })
      .finally(() => {
        changeRequestStatus('organizationLoading', STATUSPAGE.DONE);
      });
  }, []);

  // Listagem de cursos
  const requestGetAllCourses = useCallback(
    (params: GetAllCoursesUserUseCaseInterface.Params) => {
      changeRequestStatus('coursesUser', STATUSPAGE.LOADING);
      return reportService
        .getAllCoursesUser(params)
        .then((res) => {
          setCoursesList(res);
        })
        .catch((err) => console.log(err))
        .finally(() => {
          changeRequestStatus('coursesUser', STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Listagem de semestres
  const requestGetAllSemesters = useCallback(
    (props: GetAllSemestersUserUseCaseInterface.Params) => {
      changeRequestStatus('semestersLoading', STATUSPAGE.LOADING);
      reportService
        .getAllSemestersUser(props)
        .then((res) => {
          setSemestersList(res);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          changeRequestStatus('semestersLoading', STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Função para gerar o relatório de radinos
  const handleRequestReportRadino = useCallback(
    async (params: ReportRadinoUseCaseInterface.Params) => {
      const loadingKey =
        params.type === ReportType.pdf
          ? 'reportRadinoPdf'
          : 'reportRadinoExcel';
      changeRequestStatus(loadingKey, STATUSPAGE.LOADING);
      await reportService
        .reportRadino(params)
        .then((res) => {
          let blob = new Blob([res], {
            type: `application/${params?.type === ReportType.pdf ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          changeRequestStatus(loadingKey, STATUSPAGE.ERROR);
          notifyError(err.message);
        })
        .finally(() => {
          changeRequestStatus(loadingKey, STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Função para submeter o relatório com tipo específico
  const submitReport = useCallback(
    (reportType: ReportType) => {
      const values = validation.values;
      handleRequestReportRadino({
        organizationHashIds: values.organizationHashIds?.map(
          (item) => item?.value,
        ),
        courseHashIds: values.courseHashIds?.map((item) => item?.value),
        roleIds: values.roleIds?.map((item) => item?.value),
        semesterIds: values.semesterIds?.map((item) => item?.value),
        startsAt: convertDateToYYYYMMDDFormat(values.period[0]),
        endsAt: convertDateToYYYYMMDDFormat(values.period[1]),
        type: reportType,
      });
    },
    [validation.values, handleRequestReportRadino],
  );

  return {
    validation,
    semestersList,
    requestsStatus,
    courseListOptions,
    organizationOptions,
    submitReport,
    requestGetAllCourses,
    requestGetAllSemesters,
    handleRequestReportRadino,
    requestGetAllOrganizations,
  };
};
